import { Spinner } from '@/components/ui/Spinner';
import type { GoldEarnedResult } from '@/economy/types';
import type { GameOverInfo } from '@/game/GameScene';
import type { SubmitOutcome } from '@/leaderboard/types';
import { validateUsername } from '@/leaderboard/validate';
import { useEffect, useState } from 'react';

const SHARE_TOAST_MS = 2500;

interface GameOverScreenProps {
  info: GameOverInfo;
  goldEarned: GoldEarnedResult | null;
  isPersonalBest: boolean;
  previousPersonalBest: number;
  savedUsername: string | null;
  bonusActiveOnRetry: boolean;
  availableTokens: number;
  ready: boolean;
  leaderboardConfigured: boolean;
  onRetry: () => void;
  onWatchAdForRetryBonus: () => Promise<{ shown: boolean; rewarded?: boolean }>;
  onSpendTokenForBonus: () => boolean;
  onSubmitToLeaderboard: (username: string) => Promise<SubmitOutcome>;
  onShare: () => Promise<{ ok: boolean }>;
  onBackToTitle: () => void;
  adsEnabled?: boolean;
}

type SubmitState =
  | { kind: 'idle' }
  | { kind: 'naming'; value: string; error?: string }
  | { kind: 'sending' }
  | { kind: 'sent'; outcome: SubmitOutcome }
  | { kind: 'error'; message: string };

export function GameOverScreen({
  info,
  goldEarned,
  isPersonalBest,
  previousPersonalBest,
  savedUsername,
  bonusActiveOnRetry,
  availableTokens,
  ready,
  leaderboardConfigured,
  onRetry,
  onWatchAdForRetryBonus,
  onSpendTokenForBonus,
  onSubmitToLeaderboard,
  onShare,
  onBackToTitle,
  adsEnabled = true,
}: GameOverScreenProps) {
  const [adStatus, setAdStatus] = useState<'idle' | 'pending' | 'failed'>('idle');
  const [shareStatus, setShareStatus] = useState<'idle' | 'copied' | 'failed'>('idle');
  const [shareToast, setShareToast] = useState<string | null>(null);
  const [submitState, setSubmitState] = useState<SubmitState>({ kind: 'idle' });

  useEffect(() => {
    if (shareToast === null) return;
    const id = window.setTimeout(() => setShareToast(null), SHARE_TOAST_MS);
    return () => window.clearTimeout(id);
  }, [shareToast]);

  const raw = info.rawScore;
  const boostPct = Math.round(info.scoreBoostMultiplier * 100);
  const bonusMult = info.sessionBonusMultiplier;
  const final = info.score;
  const canSpendToken = availableTokens > 0 && !bonusActiveOnRetry;

  const handleAd = async () => {
    setAdStatus('pending');
    try {
      const result = await onWatchAdForRetryBonus();
      setAdStatus(result.shown && result.rewarded ? 'idle' : 'failed');
    } catch {
      setAdStatus('failed');
    }
  };

  const handleSpendToken = () => {
    onSpendTokenForBonus();
  };

  const handleShare = async () => {
    setShareStatus('idle');
    try {
      const result = await onShare();
      const ok = result.ok;
      setShareStatus(ok ? 'copied' : 'failed');
      setShareToast(ok ? 'Link copied to clipboard' : 'Copy failed — share manually');
    } catch {
      setShareStatus('failed');
      setShareToast('Copy failed — share manually');
    }
  };

  const sendWithUsername = async (username: string) => {
    setSubmitState({ kind: 'sending' });
    try {
      const outcome = await onSubmitToLeaderboard(username);
      setSubmitState({ kind: 'sent', outcome });
    } catch (error) {
      setSubmitState({
        kind: 'error',
        message: error instanceof Error ? error.message : 'Submit failed.',
      });
    }
  };

  const handleSubmitClick = () => {
    if (savedUsername) {
      void sendWithUsername(savedUsername);
      return;
    }
    setSubmitState({ kind: 'naming', value: '' });
  };

  const handleNamingSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitState.kind !== 'naming') return;
    const check = validateUsername(submitState.value);
    if (!check.ok) {
      setSubmitState({
        kind: 'naming',
        value: submitState.value,
        ...(check.message ? { error: check.message } : {}),
      });
      return;
    }
    void sendWithUsername(submitState.value.trim());
  };

  const submitDisabled =
    !ready ||
    !leaderboardConfigured ||
    submitState.kind === 'sending' ||
    submitState.kind === 'sent';
  const showSubmitButton = isPersonalBest && submitState.kind !== 'naming';

  return (
    <section className="gameover-overlay" aria-label="Game over screen">
      <div className="gameover-card">
        <header className="gameover-header">
          <h2>Time&apos;s up</h2>
          {isPersonalBest && <p className="gameover-pb">Personal best!</p>}
        </header>

        <dl className="gameover-breakdown" aria-label="Score breakdown">
          <div className="gameover-row">
            <dt>Raw score</dt>
            <dd>{raw.toLocaleString()}</dd>
          </div>
          <div className="gameover-row">
            <dt>Score boost</dt>
            <dd>+{boostPct}%</dd>
          </div>
          <div className="gameover-row">
            <dt>Session bonus</dt>
            <dd>×{bonusMult.toFixed(1)}</dd>
          </div>
          <div className="gameover-row gameover-row--final">
            <dt>Final</dt>
            <dd>{final.toLocaleString()}</dd>
          </div>
        </dl>

        <ul className="gameover-stats" aria-label="Game stats">
          <li>
            <span>Max combo</span>
            <strong>{info.maxCombo}</strong>
          </li>
          <li>
            <span>Time slimes</span>
            <strong>{info.timeSlimesCaught}</strong>
          </li>
          <li>
            <span>Play time</span>
            <strong>{info.playTimeSeconds}s</strong>
          </li>
          {info.shieldsConsumed > 0 && (
            <li>
              <span>Shields used</span>
              <strong>{info.shieldsConsumed} 🛡️</strong>
            </li>
          )}
          {goldEarned && (
            <li className="gameover-stats__gold">
              <span>Gold earned</span>
              <strong>
                +{goldEarned.totalGold} <span className="coin-icon" aria-hidden="true" />
              </strong>
            </li>
          )}
        </ul>
        {goldEarned && goldEarned.totalGold > 0 && (
          <p className="gameover-gold-breakdown" aria-label="Gold breakdown">
            Base {goldEarned.baseGold}
            {goldEarned.scoreBonus > 0 ? ` · Score +${goldEarned.scoreBonus}` : ''}
            {goldEarned.comboBonus > 0 ? ` · Combo +${goldEarned.comboBonus}` : ''}
            {goldEarned.personalBestBonus > 0 ? ` · PB +${goldEarned.personalBestBonus}` : ''}
          </p>
        )}

        <div className="gameover-actions">
          <button
            type="button"
            className="title-btn title-btn--primary"
            onClick={onRetry}
            disabled={!ready}
          >
            {bonusActiveOnRetry ? 'Retry (1.5× active)' : 'Retry'}
          </button>
          {adsEnabled &&
            (canSpendToken ? (
              <button
                type="button"
                className="title-btn title-btn--reward gameover-spend-token"
                onClick={handleSpendToken}
                disabled={!ready}
              >
                Spend 1 ⭐ → 1.5× on retry
              </button>
            ) : (
              <button
                type="button"
                className="title-btn title-btn--reward"
                onClick={handleAd}
                disabled={!ready || adStatus === 'pending' || bonusActiveOnRetry}
              >
                {adStatus === 'pending' ? (
                  <>
                    <Spinner />
                    Loading ad…
                  </>
                ) : bonusActiveOnRetry ? (
                  'Bonus active'
                ) : (
                  'Watch ad → 1.5× on retry'
                )}
              </button>
            ))}
          {showSubmitButton && (
            <button
              type="button"
              className="title-btn gameover-submit"
              onClick={handleSubmitClick}
              disabled={submitDisabled}
              data-submit-state={submitState.kind}
            >
              {submitState.kind === 'sending' ? (
                <>
                  <Spinner />
                  Submitting…
                </>
              ) : submitState.kind === 'sent' ? (
                submitState.outcome.kind === 'submitted' ? (
                  'Submitted ✓'
                ) : submitState.outcome.kind === 'not-improved' ? (
                  'Already on leaderboard'
                ) : (
                  'Try again'
                )
              ) : (
                'Submit to leaderboard'
              )}
            </button>
          )}
          <button
            type="button"
            className="title-btn gameover-share"
            onClick={handleShare}
            data-share-status={shareStatus}
          >
            Share my score
          </button>
          <button type="button" className="title-btn title-btn--ghost" onClick={onBackToTitle}>
            Back to title
          </button>
        </div>

        {submitState.kind === 'naming' && (
          <form className="gameover-naming" onSubmit={handleNamingSubmit} aria-label="Pick a name">
            <label htmlFor="gameover-username">Pick a name (3–20 chars):</label>
            <div className="gameover-naming-row">
              <input
                id="gameover-username"
                className="gameover-naming-input"
                type="text"
                minLength={3}
                maxLength={20}
                value={submitState.value}
                onChange={(e) => setSubmitState({ kind: 'naming', value: e.target.value })}
                aria-invalid={submitState.error !== undefined}
              />
              <button type="submit" className="title-btn title-btn--primary gameover-naming-submit">
                Submit
              </button>
              <button
                type="button"
                className="title-btn title-btn--ghost"
                onClick={() => setSubmitState({ kind: 'idle' })}
              >
                Cancel
              </button>
            </div>
            {submitState.error && (
              <p className="gameover-naming-error" role="alert">
                {submitState.error}
              </p>
            )}
          </form>
        )}

        {submitState.kind === 'sent' && submitState.outcome.kind === 'submitted' && (
          <output className="gameover-submit-msg">
            Submitted as {submitState.outcome.entry.username}
            {submitState.outcome.rank > 0 ? ` · rank #${submitState.outcome.rank}` : ''}.
          </output>
        )}
        {submitState.kind === 'sent' && submitState.outcome.kind === 'not-improved' && (
          <output className="gameover-submit-msg">
            Your leaderboard score is already higher (
            {submitState.outcome.existingScore.toLocaleString()}
            {submitState.outcome.rank > 0 ? `, rank #${submitState.outcome.rank}` : ''}).
          </output>
        )}
        {submitState.kind === 'sent' && submitState.outcome.kind === 'rejected' && (
          <p className="gameover-submit-msg gameover-submit-msg--err" role="alert">
            {submitState.outcome.message}
          </p>
        )}
        {submitState.kind === 'sent' && submitState.outcome.kind === 'offline' && (
          <p className="gameover-submit-msg gameover-submit-msg--err" role="alert">
            Leaderboard offline.
          </p>
        )}
        {submitState.kind === 'sent' && submitState.outcome.kind === 'error' && (
          <p className="gameover-submit-msg gameover-submit-msg--err" role="alert">
            {submitState.outcome.message}
          </p>
        )}
        {submitState.kind === 'error' && (
          <p className="gameover-submit-msg gameover-submit-msg--err" role="alert">
            {submitState.message}
          </p>
        )}
        {!leaderboardConfigured && isPersonalBest && (
          <p className="gameover-hint">Leaderboard not configured for this build.</p>
        )}
        {leaderboardConfigured && !isPersonalBest && previousPersonalBest > 0 && (
          <p className="gameover-hint">
            Beat your best ({previousPersonalBest.toLocaleString()}) to submit to the leaderboard.
          </p>
        )}

        {adStatus === 'failed' && <p className="gameover-hint">Ad not available — try again.</p>}
        {shareToast !== null && (
          <output className="gameover-toast" aria-live="polite">
            {shareToast}
          </output>
        )}
      </div>
    </section>
  );
}
